import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@rbx/intl';
import { Button, Chip, Tooltip, TTheme } from '@rbx/ui';
import TryItOutAuthMode from '../../../types/TryItOutAuthMode';
import { CSS_CLASS_NAMES, DATA_ATTRIBUTES } from '../constants';

interface AuthModeSelectorPortalProps {
  container: HTMLDivElement | null;
  authMode: TryItOutAuthMode;
  setAuthMode: (authMode: TryItOutAuthMode) => void;
  theme: TTheme;
  login?: () => void;
}

/**
 * Portal component for auth mode selector
 */
const AuthModeSelectorPortal = ({
  container,
  authMode,
  setAuthMode,
  theme,
  login,
}: AuthModeSelectorPortalProps): React.ReactElement | null => {
  const { translate } = useTranslation();
  if (!container) return null;

  const createCookieChip = (disabled: boolean, ariaLabel: string) => (
    <Chip
      variant={!disabled && authMode === TryItOutAuthMode.COOKIE ? 'filled' : 'outlined'}
      size='medium'
      color='primary'
      label={translate('Label.TryItOutAuthModeCookie')}
      {...(disabled && { disabled })}
      {...(!disabled && { onClick: () => setAuthMode(TryItOutAuthMode.COOKIE) })}
      style={
        !disabled && authMode === TryItOutAuthMode.COOKIE
          ? { color: theme.palette.content.inverse }
          : undefined
      }
      {...{ [DATA_ATTRIBUTES.SELECTED]: authMode === TryItOutAuthMode.COOKIE }}
      {...{ [DATA_ATTRIBUTES.AUTH_MODE]: TryItOutAuthMode.COOKIE }}
      aria-label={ariaLabel}
    />
  );

  const getCookieChip = () => {
    if (login) {
      return (
        <Tooltip
          title={
            <div style={{ textAlign: 'center' }}>
              <div>{translate('Description.TryItOutLoginRequired')}</div>
              <div style={{ marginTop: '8px' }}>
                <Button
                  variant='contained'
                  color='primaryBrand'
                  size='small'
                  aria-label={translate('Action.TryItOutLogIn')}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    login();
                  }}>
                  {translate('Action.TryItOutLogIn')}
                </Button>
              </div>
            </div>
          }>
          <span>{createCookieChip(true, translate('Description.TryItOutLoginRequired'))}</span>
        </Tooltip>
      );
    }

    return createCookieChip(false, translate('Label.TryItOutAuthModeCookie'));
  };

  return (
    <React.Fragment key='auth-mode-selector'>
      {createPortal(
        <div
          className={CSS_CLASS_NAMES.AUTH_MODE_SELECTOR}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: '20px',
            marginRight: '20px',
          }}>
          <span
            style={{
              fontWeight: theme.typography.fontWeightBold,
              fontSize: theme.typography.h5.fontSize,
            }}>
            {translate('Heading.TryItOutAuthMode')}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {getCookieChip()}
            <Chip
              variant={authMode === TryItOutAuthMode.NONE ? 'filled' : 'outlined'}
              size='medium'
              color='primary'
              label={translate('Label.TryItOutAuthModeNone')}
              onClick={() => setAuthMode(TryItOutAuthMode.NONE)}
              style={
                authMode === TryItOutAuthMode.NONE
                  ? { color: theme.palette.content.inverse }
                  : undefined
              }
              {...{ [DATA_ATTRIBUTES.SELECTED]: authMode === TryItOutAuthMode.NONE }}
              {...{ [DATA_ATTRIBUTES.AUTH_MODE]: TryItOutAuthMode.NONE }}
              aria-label={translate('Label.TryItOutAuthModeNone')}
            />
          </div>
        </div>,
        container,
      )}
    </React.Fragment>
  );
};

export default AuthModeSelectorPortal;
